import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { MutateKeys } from "../constants/MutateKeys";
import { QueryKeys } from "../constants/QueryKeys";
import { useMutation, useQuery } from "@tanstack/react-query";
import { generatePath } from "react-router-dom";
import { SectionAccessKeys } from "../enums/AccessPathEnums";

const RolesServices = () => {
  const {
    FETCH_USER_ROLE_DETAILS,
    ADD_USER_ROLE,
    MODIFY_ROLE_DETAILS,
    EDIT_EXTERNAL_ROLES_OPTION,
    GET_EXTERNAL_ROLES_LIST,
  } = ApiRoutes;
  const { FETCH_USER_ROLE_DATA, FETCH_EXTERNAL_ROLES_KEY } = QueryKeys;
  const {
    MODIFY_ROLES,
    ADD_EXTERNAL_ROLE_KEY,
    EDIT_EXTERNAL_ROLE_KEY,
    DELETE_EXTERNAL_ROLE_KEY,
  } = MutateKeys;
  const FetchUserRoleDetails = (userRoleInfo: string) => {
    const fetchUserRoleDetails = async () => {
      const url = `${FETCH_USER_ROLE_DETAILS}?id=${userRoleInfo}`;
      const { data } = await axiosInstance.get(url);
      return data;
    };
    return useQuery({
      queryKey: [FETCH_USER_ROLE_DATA],
      queryFn: fetchUserRoleDetails,
      refetchOnMount: true,
      enabled: !!userRoleInfo,
      refetchOnWindowFocus: false,
    });
  };

  const AddUserRoleDetails = () => {
    const AddUserRoleDetails = async (payloadData) => {
      const payload = {
        entityData: {
          ...payloadData,
        },
      };
      const { data } = await axiosInstance.post(ADD_USER_ROLE, payload);
      return data;
    };
    return useMutation({
      mutationKey: [MODIFY_ROLES],
      mutationFn: AddUserRoleDetails,
    });
  };

  const UpdateUserRoleDetails = (id) => {
    const UpdateUserRoleDetails = async (payloadData) => {
      const payload = {
        entityData: {
          ...payloadData,
        },
      };
      const url = generatePath(MODIFY_ROLE_DETAILS, {
        id: id,
      });
      const { data } = await axiosInstance.put(url, payload);
      return data;
    };
    return useMutation({
      mutationKey: [MODIFY_ROLES],
      mutationFn: UpdateUserRoleDetails,
    });
  };

  const FetchExternalRolesList = () => {
    const fetchExternalRolesList = async () => {
      const { data } = await axiosInstance.get(GET_EXTERNAL_ROLES_LIST);
      return data?.data;
    };

    return useQuery({
      queryKey: [FETCH_EXTERNAL_ROLES_KEY],
      queryFn: fetchExternalRolesList,
      refetchOnWindowFocus: false,
    });
  };

  const AddExternalRoleEntry = ({ isCrm = false }: { isCrm?: boolean }) => {
    const addExternalRoleEntry = async (roleName: string) => {
      const payload = {
        entityData: {
          roleName: roleName,
        },
      };
      const params = isCrm ? {
        section: SectionAccessKeys.CRM,
      } : {}
      const { data } = await axiosInstance.post(
        GET_EXTERNAL_ROLES_LIST,
        payload,
        {
          params: params
        }
      );
      return data;
    };

    return useMutation({
      mutationKey: [ADD_EXTERNAL_ROLE_KEY],
      mutationFn: addExternalRoleEntry,
    });
  };

  const EditExternalRoleEntry = () => {
    const editExternalRoleEntry = async ({
      roleName,
      roleId,
    }: {
      roleName: string;
      roleId: string;
    }) => {
      const payload = {
        entityData: {
          roleName: roleName,
        },
      };
      const endpoint = generatePath(EDIT_EXTERNAL_ROLES_OPTION, { id: roleId });
      const { data } = await axiosInstance.put(endpoint, payload);
      return data;
    };

    return useMutation({
      mutationKey: [ADD_EXTERNAL_ROLE_KEY],
      mutationFn: editExternalRoleEntry,
    });
  };

  const DeleteExternalRoleEntry = () => {
    const deleteExternalRoleEntry = async (roleId: string) => {
      const endpoint = generatePath(EDIT_EXTERNAL_ROLES_OPTION, { id: roleId });
      const { data } = await axiosInstance.delete(endpoint);
      return data;
    };

    return useMutation({
      mutationKey: [ADD_EXTERNAL_ROLE_KEY],
      mutationFn: deleteExternalRoleEntry,
    });
  };

  return {
    FetchUserRoleDetails,
    AddUserRoleDetails,
    UpdateUserRoleDetails,
    FetchExternalRolesList,
    AddExternalRoleEntry,
    EditExternalRoleEntry,
    DeleteExternalRoleEntry,
  };
};

export default RolesServices;
